import type { DuplicateGroup, PhotoDecisions } from "./types";

export type GroupComparison = {
  evidenceGap: number;
  exactBytes: boolean;
  exactDecodedPixels: boolean;
  sameDimensions: boolean;
  sameCaptureTime: boolean;
  byteDifference: number;
};

export function confirmedOriginalId(group: DuplicateGroup): string | undefined {
  if (group.manualOriginalId) return group.manualOriginalId;
  return group.confidence === "review" ? undefined : group.recommendedId;
}

export function chooseOriginal(group: DuplicateGroup, photoId: string, decisions: PhotoDecisions): boolean {
  if (!group.items.some((photo) => photo.id === photoId)) return false;
  if (["quarantined", "restored", "deleted"].includes(decisions[photoId])) return false;
  group.manualOriginalId = photoId;
  for (const photo of group.items) {
    if (["quarantined", "restored", "deleted"].includes(decisions[photo.id])) continue;
    decisions[photo.id] = photo.id === photoId ? "keep" : "quarantine";
  }
  return true;
}

export function groupComparison(group: DuplicateGroup): GroupComparison {
  const scores = group.items
    .map((photo) => group.evidence[photo.id]?.score ?? Number.NEGATIVE_INFINITY)
    .sort((left, right) => right - left);
  const hashes = group.items.map((photo) => photo.exactHash).filter(Boolean);
  const pixelHashes = group.items.map((photo) => photo.pixelHash).filter(Boolean);
  const dimensions = new Set(group.items.map((photo) => `${photo.width}x${photo.height}`));
  const captureTimes = group.items.map((photo) => photo.takenAt).filter(Boolean);
  const sizes = group.items.map((photo) => photo.file.size);
  return {
    evidenceGap: scores.length > 1 ? Math.max(0, scores[0] - scores[1]) : 0,
    exactBytes: hashes.length === group.items.length && new Set(hashes).size === 1,
    exactDecodedPixels: pixelHashes.length === group.items.length && new Set(pixelHashes).size === 1,
    sameDimensions: dimensions.size === 1,
    sameCaptureTime: captureTimes.length === group.items.length && new Set(captureTimes).size === 1,
    byteDifference: sizes.length ? Math.max(...sizes) - Math.min(...sizes) : 0,
  };
}
